import { useEffect } from "react";
import { Footer, Nav } from "./Layout";
import { COMPANY, COMPANY_URL, CONTACT_EMAIL, PRODUCT } from "./content";

/* Terms and privacy.

   THESE ARE A STARTING DRAFT, NOT LEGAL ADVICE. They describe what the
   product actually does with data - accurately, as of this version - but they
   should be reviewed by a lawyer for your jurisdiction before launch.

   The privacy page includes data-deletion instructions because Meta's app
   review requires a URL for them alongside the privacy policy. */

const UPDATED = "10 October 2026";

const PRIVACY = [
  ["Who we are", [
    `${PRODUCT} is a product owned and developed by ${COMPANY} ("${COMPANY}", "we", "us"), which is responsible for the personal information processed through it.`,
    `${PRODUCT} provides software that plans, writes, designs and publishes social media posts and website articles on behalf of the workspaces that use it. This policy explains what we collect, why, and the choices you have.`,
  ]],
  ["What we collect", [
    "Account information: your name, email address and a one-way hash of your password. We never store your password itself.",
    "Workspace content: the brands, guidelines, logos, posts, images, captions, feeds, articles and settings you create or upload.",
    "Connected accounts: when you connect LinkedIn, Facebook, Instagram or a WordPress site, we store the access tokens or application passwords those services issue, so we can publish on your instruction. We do not receive your passwords for those services.",
    "Usage and technical data: what you generate and publish (to enforce plan limits), server logs, and, where you enable link tracking, the number of clicks a link receives. We do not track individual visitors across the web.",
    "Browser storage: your session token is kept in your browser's local storage so you stay signed in. We do not use advertising cookies.",
  ]],
  ["How we use it", [
    "To provide the service: generating content, rendering images, scheduling, and publishing to the accounts you connected, when you or your workspace's approval settings say so.",
    "To keep it secure and working: authentication, abuse prevention, debugging, and enforcing plan limits.",
    "To contact you about your account, and - only if you joined the waitlist or opted in - about the product.",
  ]],
  ["AI processing", [
    "To generate captions, images and articles, the relevant content (for example your brand guidelines and the topic or article being written about) is sent to third-party AI model providers. They process it to return the result. We do not use your content to train models.",
  ]],
  ["Who we share it with", [
    "The platforms you connect - LinkedIn, Meta (Facebook and Instagram) and your WordPress sites - receive the posts you publish to them.",
    "AI model providers, as described above, and the infrastructure providers that host our servers and database.",
    "Approval channels you configure, such as Telegram or WhatsApp (via Twilio), receive the post previews sent for approval.",
    "We do not sell your personal information, and we do not share it for advertising.",
  ]],
  ["Retention and deletion", [
    "We keep your data while your account is active. If you delete your workspace or ask us to delete your account, we delete its content and connected-account tokens within 30 days, except where we must keep limited records by law.",
    `Data deletion requests: email ${CONTACT_EMAIL} from the address on your account with the subject "Delete my data". We will confirm the deletion. To revoke our access to a social account immediately, disconnect it in ${PRODUCT} or remove the app in that platform's settings (for Facebook: Settings & Privacy → Settings → Apps and Websites).`,
  ]],
  ["Security", [
    "Each workspace's data is isolated at the database layer. Passwords are hashed with bcrypt, connections are encrypted in transit, and access is protected by signed, expiring session tokens. No system is perfectly secure, but we take reasonable measures to protect your data.",
  ]],
  ["Your rights", [
    `Depending on where you live, you may have the right to access, correct, export or delete your personal information, or to object to its processing. Contact ${CONTACT_EMAIL} and we will respond within the time the law requires.`,
  ]],
  ["Children", [`${PRODUCT} is not intended for anyone under 16, and we do not knowingly collect their data.`]],
  ["Changes", ["If we change this policy materially, we will notify account owners by email or in the app before the change takes effect."]],
  ["Contact", [`Questions about privacy: ${CONTACT_EMAIL}.`]],
];

const TERMS = [
  ["Agreement", [
    `${PRODUCT} is a product owned, developed and operated by ${COMPANY} ("${COMPANY}", "we", "us"). These terms are an agreement between you and ${COMPANY}.`,
    `By creating an account or using ${PRODUCT}, you agree to these terms on behalf of yourself and the organisation whose workspace you use. If you do not agree, do not use the service.`,
  ]],
  ["The service", [
    `${PRODUCT} generates, schedules and publishes content to accounts you connect. Features, limits and availability may change as the product develops. Plans and their limits are described on our pricing page.`,
  ]],
  ["Your account", [
    "You are responsible for keeping your credentials secure and for everything done under your account and in your workspace, including by teammates you invite.",
  ]],
  ["Your content and responsibility for publishing", [
    "You own the content you provide and the content generated for you. You grant us the licence needed to store, process and publish it on your instruction.",
    "Generated content can be wrong. You are responsible for what is published from your workspace - including posts published automatically under approval settings you chose. Use manual approval if you want to review everything before it goes live.",
  ]],
  ["Acceptable use", [
    "Do not use the service to publish unlawful, defamatory, infringing, deceptive or harassing content; to spam; to impersonate others; or in breach of the rules of the platforms you publish to. We may suspend workspaces that do.",
  ]],
  ["Third-party platforms", [
    "Publishing depends on LinkedIn, Meta, WordPress and other services we do not control. Their outages, rule changes or decisions about your accounts are outside our responsibility.",
  ]],
  ["Fees", ["Paid plans are billed as described at purchase. Usage limits reset monthly. We may change prices with notice before your next billing period."]],
  ["Ownership", [
    `${PRODUCT}, its software, design and brand are the property of ${COMPANY}. These terms give you the right to use the service; they do not transfer any ownership of it to you.`,
  ]],
  ["Termination", ["You can stop using the service at any time. We may suspend or end access for breach of these terms. On termination we delete your data as described in our privacy policy."]],
  ["Disclaimers and liability", [
    'The service is provided "as is". To the extent the law allows, we disclaim implied warranties, and our total liability for any claim is limited to the amount you paid us in the 12 months before it arose. We are not liable for indirect or consequential losses, including lost reach or revenue.',
  ]],
  ["Changes", ["We may update these terms. Material changes will be notified in advance; continuing to use the service after they take effect means you accept them."]],
  ["Contact", [`${CONTACT_EMAIL}`]],
];

export default function Legal({ kind }) {
  const doc = kind === "privacy" ? PRIVACY : TERMS;
  const title = kind === "privacy" ? "Privacy Policy" : "Terms of Service";
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className="min-h-screen bg-ac-bg text-ac-text">
      <Nav signupsOpen={false} signedIn={false} />
      <main className="mx-auto max-w-3xl px-4 sm:px-8 pt-32 pb-24">
        <h1 className="font-display text-[44px] sm:text-[56px] font-semibold leading-[1.05] text-ac-text">{title}</h1>
        <p className="mt-3 text-sm text-ac-muted">
          Last updated {UPDATED} · {PRODUCT} is a product of{" "}
          <a href={COMPANY_URL} target="_blank" rel="noreferrer" className="text-ac-link underline">{COMPANY}</a>
        </p>
        <div className="mt-12 space-y-10">
          {doc.map(([heading, paras], i) => (
            <section key={heading}>
              <h2 className="font-display text-[22px] font-semibold text-ac-text">{i + 1}. {heading}</h2>
              <div className="mt-3 space-y-3">
                {paras.map((t) => <p key={t.slice(0, 40)} className="text-ac-muted leading-relaxed">{t}</p>)}
              </div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
